import { call, put, select } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import api from '../../../../../../services/api';
import * as Sentry from '@sentry/react';
import { notification } from 'antd';
import { CustomFormsState } from '../reducers';
import { getCustomForms } from '../selectors';
import { setState } from '../actions';
import { CustomFormTemplate } from '../../types/template';
import { validateAllConditions } from '../../components/Templates/FRAT/validation';

export function* saveTemplateWorker() {
  const { editingTemplate, templateList } = (yield select(getCustomForms)) as CustomFormsState;

  if (editingTemplate.Questions && Array.isArray(editingTemplate.Questions)) {
    const validationResult = validateAllConditions(editingTemplate.Questions);

    if (!validationResult.isValid && validationResult.criticalErrors > 0) {
      notification.error({
        message: 'Validation Error',
        description: `Cannot save form: ${
          validationResult.criticalErrors
        } critical validation error${
          validationResult.criticalErrors !== 1 ? 's' : ''
        } found. Please fix invalid condition references before saving.`,
        duration: 5,
      });
      return;
    }

    if (!validationResult.isValid && validationResult.warnings > 0) {
      notification.warning({
        message: 'Validation Warning',
        description: `Form saved with ${validationResult.warnings} warning${
          validationResult.warnings !== 1 ? 's' : ''
        }. Please review condition references.`,
        duration: 4,
      });
    }
  }

  yield put(setState({ isLoading: true }));

  try {
    let response: AxiosResponse;

    if (!editingTemplate.ID) {
      response = yield call(api.post, `/v1/custom-forms/template`, editingTemplate);
    } else {
      response = yield call(
        api.patch,
        `/v1/custom-forms/template/${editingTemplate.ID}`,
        editingTemplate,
      );
    }

    if (response.status === 200) {
      const newTemplate = response.data.Data as CustomFormTemplate;

      if (editingTemplate.CertificateIDs !== undefined) {
        yield call(api.patch, `/v1/custom-forms/template/${newTemplate.ID}/patch-certificates`, {
          CertificateIDs: editingTemplate.CertificateIDs,
        });
      }

      // The template save response doesn't include CertificateIDs (populated separately)
      const prevCertIDs = templateList.find(t => t.ID === newTemplate.ID)?.CertificateIDs ?? [];
      const templateWithCerts: CustomFormTemplate = {
        ...newTemplate,
        CertificateIDs: editingTemplate.CertificateIDs ?? prevCertIDs,
      };

      let updatedTemplateList: CustomFormTemplate[];
      if (!editingTemplate.ID) {
        updatedTemplateList = [...templateList, templateWithCerts];
      } else {
        updatedTemplateList = templateList.map(t =>
          t.ID === templateWithCerts.ID ? templateWithCerts : t,
        );
      }

      yield put(
        setState({ templateList: updatedTemplateList, editingTemplate: templateWithCerts }),
      );

      notification.success({
        message: 'Success',
        description: 'Form template has been saved successfully.',
      });
    }
  } catch (error) {
    Sentry.captureMessage(`Error creating form template ${error}`);
    notification.error({
      message: 'Error',
      description:
        'There was an error creating/updating form template. Please try again or contact support.',
    });
  } finally {
    yield put(setState({ isLoading: false, isTemplateModalOpen: false }));
  }
}
